/**
 * Homepage hero.
 *
 * Restyled 2026-10-24 in direction B, "Bright and bold", the look the owner picked (docs/design/MASTER.md section 2a,
 * docs/design/pages/home.md): soft blue, violet and teal glows behind the hero (CSS gradients, .home-hero in
 * index.css), a white pill eyebrow, the headline in Inter Black with "Biohackers" in the brand colour over a violet
 * swoosh, a white search box with a soft blue shadow, white chips, a solid store button with a coloured shadow, the
 * code on a highlighter mark, and from 1024px a turning DNA helix with two floating cards of real numbers (HeroArt.tsx);
 * below 1024px a slim moving helix sits between the code line and the cheat sheet (HelixCanvas.tsx).
 *
 * The pieces are the ones the owner keeps on the first screen: the headline, the search box, the popular chips,
 * "Shop BHG Labs" (solid) and "Compare prices" (outline), the BHG20 line and the cheat sheet form. The cheat sheet
 * card sits under the hero's two columns from 1024px, as a strip.
 *
 * The headline (home/HeroTitle.tsx) is built so that it never moves when Inter replaces the fallback font; its
 * comment says how.
 *
 * Earlier history that still holds (2026-09-30):
 *  - It is rendered statically with the page, not lazy-loaded behind a fallback (the old swap was most of the
 *    homepage's layout shift).
 *  - One headline. The three-way headline test could never pick a winner and made two in three visitors see the
 *    headline change under them.
 *  - The cheat sheet capture is an inline form. It was the homepage's best signup source (827 subscribers tagged
 *    hero-cheatsheet-gate, against 12 from the newsletter box at the bottom of the page). Same source string, so the
 *    Beehiiv automation and the peptide-intent segment treat these signups exactly as before.
 *  - Submitting the form starts the PDF download on the spot.
 *
 * KEEP IN SYNC with the static shell in index.html (#app-loader .shell-home). That shell paints this same markup,
 * with the same classes, before any JavaScript runs, so the swap to this component moves nothing. If you change
 * copy, classes, sizes or spacing here, change them there. scripts/dev/home-check.mjs measures both, and
 * src/components/home/heroShell.test.tsx compares the headline and the helix column.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Download, FlaskConical, Search } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import CompoundSearchCombobox from "@/components/CompoundSearchCombobox";
import SearchSheet from "@/components/SearchSheet";
import HeroArt from "@/components/home/HeroArt";
import HelixCanvas from "@/components/home/HelixCanvas";
import HeroTitle from "@/components/home/HeroTitle";
import { useCompoundSearchIndex, useListings, useVendors } from "@/hooks/useDatabase";
import { captureEmail } from "@/lib/captureEmail";
import { LEAD_VALUE_TIERS } from "@/lib/constants";
import { storeUrl, STORE_COUPON } from "@/lib/attribution";
import { trackAffiliateClick } from "@/lib/analytics";
import { CHEAT_SHEET_PAGE, CHEAT_SHEET_SUMMARY, downloadCheatSheet, isSubscribed, markSubscribed } from "@/lib/cheatSheet";

const emailSchema = z.string().trim().email("Please enter a valid email").max(255);
const HERO_SEARCH_INPUT_ID = "hero-search-input";

// One-tap shortcuts under the search box. Static on purpose: a list that fills in after a
// request would move the buttons below it. A phone shows the three marked "phone" so the row
// stays on one line; wider screens show all five.
// Each chip has a fixed width: the wider of its name in Inter and in "Inter Fallback" at 13px, plus
// the padding. With natural widths the chips after a name moved 2 to 7px when Inter replaced the
// fallback. Change a name and you change its width here and in index.html.
const POPULAR = [
  { name: "Retatrutide", slug: "retatrutide", phone: true, w: "w-[5.875rem]" },
  { name: "BPC-157", slug: "bpc-157", phone: true, w: "w-[5.125rem]" },
  { name: "Tirzepatide", slug: "tirzepatide", phone: false, w: "w-[5.875rem]" },
  { name: "Semax", slug: "semax", phone: true, w: "w-[4.25rem]" },
  { name: "GHK-Cu", slug: "ghk-cu", phone: false, w: "w-[4.875rem]" },
];

export default function HeroSection() {
  const navigate = useNavigate();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // "no": not subscribed. "known": this browser subscribed on an earlier visit. "new": signed up just now.
  // "returning": typed an address we already had (the download still starts, but no fresh email is promised).
  const [subscriber, setSubscriber] = useState<"no" | "known" | "new" | "returning">(() => (isSubscribed() ? "known" : "no"));

  // The phone sheet asks for the light search index (the same one the navbar search uses) only once it opens.
  // Vendors and listings are already loaded by the price blocks further down this page.
  const { data: compounds = [] } = useCompoundSearchIndex(mobileSearchOpen);
  const { data: vendors = [] } = useVendors();
  const { data: listings = [] } = useListings();

  const closeMobileSearch = () => {
    setQuery("");
    setMobileSearchOpen(false);
  };

  // "/" jumps to the search box, the way it does on most reference sites.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      const input = document.getElementById(HERO_SEARCH_INPUT_ID) as HTMLInputElement | null;
      // The desktop box is display:none on a phone, where offsetParent is null.
      if (input && input.offsetParent !== null) {
        e.preventDefault();
        input.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const topMatch =
    query.trim().length >= 2
      ? compounds.find(
          (c) =>
            c.name.toLowerCase().includes(query.toLowerCase().trim()) ||
            (c.aliases || []).some((a) => a.toLowerCase().includes(query.toLowerCase().trim())),
        )
      : null;

  const handleCheatSheetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    try {
      const validEmail = emailSchema.parse(email);
      setSubmitting(true);
      // Same source as the modal this form replaced, so sync-to-beehiiv enrols the subscriber in
      // the cheat sheet automation and the peptide-intent segment picks them up. The extra tag
      // only marks which version of the capture they used.
      const result = await captureEmail({
        email: validEmail,
        source: "hero-cheatsheet-gate",
        tags: ["homepage-cheatsheet-inline"],
        trackLead: {
          content_id: "hero-cheatsheet-gate",
          content_name: "Hero Cheatsheet Gate",
          content_category: "lead-magnet",
          value: LEAD_VALUE_TIERS.leadMagnet,
          currency: "USD",
        },
      });
      if (!result.ok) {
        toast.error(result.error ?? "Something went wrong");
        setSubmitting(false);
        return;
      }
      markSubscribed();
      downloadCheatSheet("hero-cheatsheet-gate");
      setEmail("");
      setSubscriber(result.inserted ? "new" : "returning");
      setSubmitting(false);
    } catch (err: unknown) {
      const message = err instanceof z.ZodError ? err.errors[0]?.message : err instanceof Error ? err.message : null;
      toast.error(message || "Something went wrong");
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="home-hero">
        <div className="mx-auto w-full max-w-6xl px-4 pb-10 pt-20 sm:px-6 lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-center lg:gap-x-12 lg:px-8 lg:pb-16 lg:pt-24 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0">
            <p className="type-label inline-flex h-7 items-center gap-2 rounded-full bg-card px-3 font-semibold text-primary shadow-sm ring-1 ring-primary/15">
              <FlaskConical className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span aria-hidden="true">Peptides · Nootropics · Vendor prices</span>
              <span className="sr-only">Peptides, nootropics and vendor prices</span>
            </p>
            <HeroTitle />
            {/* No count here: the static shell in index.html paints this same line before any data loads, and a
                number typed into the shell goes stale as compounds are added (it said 273 beside a live 286). The
                live count is in "How this site works" further down. */}
            <p className="mt-3 max-w-xl text-lg text-foreground/80 sm:mt-6">
              Compare vendor prices, run the reconstitution math and read the research on every compound we track.
            </p>

            {/* Search, the main action. A real input from 640px up; on a phone it opens a full-screen sheet so the
                results are not squeezed under the keyboard. */}
            <div className="mt-7 max-w-2xl">
              <button
                type="button"
                onClick={() => setMobileSearchOpen(true)}
                className="home-card home-card-blue flex h-14 w-full items-center gap-3 px-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:hidden"
              >
                <Search className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                <span className="type-body text-muted-foreground">Search compounds, vendors, tools</span>
              </button>
              <div className="hidden sm:block">
                <CompoundSearchCombobox
                  size="lg"
                  inputId={HERO_SEARCH_INPUT_ID}
                  placeholder={`Search compounds: Semax, BPC-157, Lion's Mane (press "/")`}
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="type-label w-[3.25rem] font-normal text-foreground/80">Popular:</span>
              {POPULAR.map((c) => (
                <Link
                  key={c.slug}
                  to={`/compound/${c.slug}`}
                  className={`${c.phone ? "inline-flex" : "hidden sm:inline-flex"} ${c.w} type-label h-11 items-center justify-center rounded-full bg-card font-normal text-foreground shadow-sm ring-1 ring-border transition-shadow duration-150 hover:ring-foreground/30`}
                >
                  {c.name}
                </Link>
              ))}
            </div>

            {/* Store first, compare second, as the owner set them: the store solid, compare outlined. A plain <a> for
                the store because it leaves the site; attribution.ts adds the stored ad parameters, ?ref and the
                coupon to the URL. */}
            <div className="mt-7 grid max-w-md grid-cols-2 gap-3 sm:flex sm:max-w-none">
              <a
                href={storeUrl("homepage_hero")}
                rel="sponsored"
                onClick={() => trackAffiliateClick({ vendor: "bhg-labs", placement: "homepage_hero" })}
                className="inline-flex h-12 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition-colors duration-150 hover:bg-primary/90 sm:w-[12.5rem] sm:px-6 sm:text-base"
              >
                Shop BHG Labs <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <Link
                to="/compare"
                className="inline-flex h-12 items-center justify-center whitespace-nowrap rounded-xl bg-card px-4 text-sm font-semibold text-foreground shadow-sm ring-1 ring-border transition-colors duration-150 hover:bg-secondary sm:w-[12.5rem] sm:px-6 sm:text-base"
              >
                Compare prices
              </Link>
            </div>
            {/* The first two pieces sit in boxes as wide as the wider of Inter and "Inter Fallback" (with the spaces),
                so the code does not slide sideways when Inter replaces the fallback. Same in index.html. */}
            <p className="type-small mt-4 leading-6 text-foreground/80">
              <span className="inline-block w-40 font-semibold text-foreground">20% off your first order</span>
              <span className="inline-block w-[4.625rem] text-center">with code</span>
              <span className="code-mark">{STORE_COUPON}</span>
            </p>
            {/* Below 1024px, where the helix column is hidden: a slim moving helix between the code line and the cheat
                sheet (HelixCanvas). The shell reserves the same box, so it adds no shift; nothing sits over it. */}
            <div aria-hidden="true" className="relative mt-6 h-14 lg:hidden">
              <HelixCanvas orientation="horizontal" />
            </div>
          </div>

          <HeroArt />

          {/* Cheat sheet capture: a card under the text on a phone, a strip under both columns from 1024px. Three
              states, all the same height so nothing below moves: the email form, a download button for a browser
              that subscribed earlier, and the same button right after a signup. The shell in index.html has the
              first two. */}
          <div
            data-hero="sheet"
            className="home-card home-card-blue mt-6 p-4 sm:max-w-2xl sm:p-5 lg:col-span-2 lg:mt-12 lg:grid lg:max-w-none lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:items-center lg:gap-x-10 lg:p-6"
          >
            <div className="flex items-start gap-4 lg:items-center">
              <div className="flex shrink-0 gap-3">
                <img
                  src="/images/cheat-sheet-page-1.webp"
                  alt="The cover of the peptide cheat sheet PDF"
                  width={640}
                  height={828}
                  loading="lazy"
                  decoding="async"
                  className="h-[62px] w-12 rounded border border-border object-cover lg:h-[104px] lg:w-20"
                />
                <img
                  src="/images/cheat-sheet-page-3.webp"
                  alt="A table page from the cheat sheet: compound, vial, BAC water, dose, cycle and bloodwork"
                  width={640}
                  height={828}
                  loading="lazy"
                  decoding="async"
                  className="hidden h-[104px] w-20 rounded border border-border object-cover lg:block"
                />
              </div>
              <div className="min-w-0">
                <h2 className="type-body font-bold text-foreground">Free peptide cheat sheet (PDF)</h2>
                <p className="type-small mt-1 text-muted-foreground">{CHEAT_SHEET_SUMMARY}</p>
              </div>
            </div>
            <div className="min-w-0">
              {subscriber !== "no" ? (
                <button
                  type="button"
                  onClick={() => downloadCheatSheet("hero-cheatsheet-subscriber")}
                  className="mt-4 inline-flex h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-foreground px-5 text-sm font-semibold text-background transition-opacity duration-150 hover:opacity-90 lg:mt-0"
                >
                  <Download className="h-4 w-4" aria-hidden="true" /> Download the PDF
                </button>
              ) : (
                <form onSubmit={handleCheatSheetSubmit} className="mt-4 flex gap-2 lg:mt-0">
                  <label htmlFor="hero-cheatsheet-email" className="sr-only">
                    Email address for the cheat sheet
                  </label>
                  <input
                    id="hero-cheatsheet-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="type-body h-11 min-w-0 flex-1 rounded-xl border border-input bg-background px-3 text-foreground outline-none placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex h-11 shrink-0 items-center justify-center rounded-xl bg-foreground px-4 text-sm font-semibold text-background transition-opacity duration-150 hover:opacity-90 disabled:opacity-60"
                  >
                    {submitting ? "Sending" : "Send it"}
                  </button>
                </form>
              )}
              <p className="type-small mt-3 text-muted-foreground" role={subscriber === "new" || subscriber === "returning" ? "status" : undefined}>
                {subscriber === "new" || subscriber === "returning" ? (
                  <>
                    Your download has started.{subscriber === "new" ? " We emailed you a copy too." : ""}{" "}
                    <Link to={CHEAT_SHEET_PAGE} className="font-medium text-primary underline-offset-4 hover:underline">
                      See what is in it
                    </Link>
                  </>
                ) : subscriber === "known" ? (
                  "You are already subscribed."
                ) : (
                  "We email you the PDF. Unsubscribe anytime."
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Phone search sheet (shared with the navbar search below 640px) */}
      {mobileSearchOpen && (
        <SearchSheet
          query={query}
          onQueryChange={setQuery}
          onClose={closeMobileSearch}
          onEnter={() => {
            if (!topMatch) return;
            closeMobileSearch();
            navigate(`/compound/${topMatch.slug}`);
          }}
          compounds={compounds}
          vendors={vendors}
          listings={listings}
        />
      )}
    </>
  );
}
